import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { InfoIcon } from "lucide-react"
import { CalculatorFormData, CalculatorProvince } from "@/lib/types/calculator"

interface PaidTimeOffSectionProps {
  form: CalculatorFormData;
  selectedProvince: CalculatorProvince;
  onFormChange: (field: string, value: string | boolean) => void;
}

export function PaidTimeOffSection({
  form,
  selectedProvince,
  onFormChange
}: PaidTimeOffSectionProps) {
  return (
    <Card>
      <CardContent className="pt-6">
        <h3 className="text-lg font-medium mb-4">Paid Time Off</h3>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="vacationDays" className="flex items-center">
              Vacation Days (per year)
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <InfoIcon className="h-4 w-4 ml-1 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Typically 10 days (2 weeks) or 15 days (3 weeks) after 5 years in most provinces</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </Label>
            <Input
              id="vacationDays"
              type="number"
              value={form.vacationDays}
              onChange={(e) => onFormChange("vacationDays", e.target.value)}
              placeholder="e.g. 10"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="sickDays" className="flex items-center">
              Sick Days (per year)
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <InfoIcon className="h-4 w-4 ml-1 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Number of paid sick days provided per year</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </Label>
            <Input
              id="sickDays"
              type="number"
              value={form.sickDays}
              onChange={(e) => onFormChange("sickDays", e.target.value)}
              placeholder="e.g. 5"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="numPublicHolidays" className="flex items-center">
              Public Holidays (per year)
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <InfoIcon className="h-4 w-4 ml-1 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Statutory holidays in your province (defaults based on province)</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </Label>
            <Input
              id="numPublicHolidays"
              type="number"
              value={form.numPublicHolidays}
              onChange={(e) => onFormChange("numPublicHolidays", e.target.value)}
              placeholder={`e.g. ${selectedProvince.defaultPublicHolidays}`}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="otherPaidLeave" className="flex items-center">
              Other Paid Leave (per year)
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <InfoIcon className="h-4 w-4 ml-1 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Personal days, parental leave, bereavement, etc.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </Label>
            <Input
              id="otherPaidLeave"
              type="number"
              value={form.otherPaidLeave}
              onChange={(e) => onFormChange("otherPaidLeave", e.target.value)}
              placeholder="e.g. 3"
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
} 